import { registerWidget, registerHintWidget, type CellContentProps } from '../registry'
import { declaredUnit, formatCalculatedValue, formatValue } from '../../views/format'
import { getLocale } from '../../locale'

/**
 * FormattedNumberContent — generic read cell for number-family types.
 * Kinds: numeric (unit), percentage (percentage), measurement (unit), and
 * calculated's read (CalculatedContent, registered in formatted-string.tsx:
 * a number with its formula's unit, SEN-226).
 * All are read-only in Phase 1 (no write editor).
 */

/** A number with its declared unit, printed by ``format``: a stored value
 *  shows what was saved (formatValue); a calculated one by magnitude. */
function makeUnitContent(format: (n: number, locale?: string) => string) {
  return function UnitContent({ value, config }: CellContentProps) {
    if (value == null) return <span className="text-muted-foreground">—</span>
    const raw = typeof value === 'object' && value !== null
      ? (value as Record<string, unknown>)['value']
      : value
    if (raw == null || raw === '') return <span className="text-muted-foreground">—</span>
    const n = typeof raw === 'number' ? raw : Number(raw)
    if (Number.isNaN(n)) return <span className="text-muted-foreground">—</span>
    const unit = declaredUnit(value, config)
    return (
      <span className="tabular-nums">
        {format(n, getLocale())}
        {unit && <span className="ml-1 text-muted-foreground">{unit}</span>}
      </span>
    )
  }
}

const UnitContent = makeUnitContent(formatValue)
/** SEN-226: a calculated value — decimals by magnitude, the formula's unit. */
export const CalculatedContent = makeUnitContent(formatCalculatedValue)

function PercentageContent({ value }: CellContentProps) {
  if (value == null) return <span className="text-muted-foreground">—</span>
  const v = typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null
  if (!v) return <span className="text-muted-foreground">—</span>
  const n = Number(v['value'])
  const prec = typeof v['precision'] === 'number' ? v['precision'] : 1
  if (Number.isNaN(n)) return <span className="text-muted-foreground">—</span>
  return <span className="tabular-nums">{n.toFixed(prec)}%</span>
}

registerWidget('numeric',     { read: UnitContent,       align: 'right', defaultWidth: 110, showsUnit: true })
registerWidget('percentage',  { read: PercentageContent, align: 'right', defaultWidth: 90 })
registerWidget('measurement', { read: UnitContent,       align: 'right', defaultWidth: 110, showsUnit: true })

registerHintWidget('unit',       { read: UnitContent,       align: 'right', defaultWidth: 110, showsUnit: true })
registerHintWidget('percentage', { read: PercentageContent, align: 'right', defaultWidth: 90 })
